import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import Snap from 'snapsvg';
import distinctColors from 'distinct-colors';
import { connect } from 'react-redux';
import { Observable } from 'rxjs';
import { chunk } from 'lodash';

import FlatButton from 'material-ui/FlatButton';
import { teal300, red300 } from 'material-ui/styles/colors';

import { SingleImageEvaluationHints as Hints } from '../../resources/hints';
import { fullPathSelector } from '../../selectors/RouterSelector';
import ListItemDrawer from '../../components/ListItemDrawer';
import AppSwitch from '../../components/AppSwitch';
import './SegmentationSVG.sass';

class SegmentationSVG extends Component {
  constructor(props) {
    super(props);

    this.handleToggleCategory = this.handleToggleCategory.bind(this);
    this.handleSwitchToEvaluationPage = this.handleSwitchToEvaluationPage.bind(this);

    /* Visualization Configuration Methods */
    this.handleToggleSegmentVisibility = this.handleToggleSegmentVisibility.bind(this);
    this.handleToggleBoundingBoxVisibility = this.handleToggleBoundingBoxVisibility.bind(this);
    this.handleShowAllCategories = this.handleShowAllCategories.bind(this);
    this.handleHideAllCategories = this.handleHideAllCategories.bind(this);
    this.handleZoom = this.handleZoom.bind(this);
    this.handleIncreaseBrightness = this.handleIncreaseBrightness.bind(this);
    this.handleDecreaseBrightness = this.handleDecreaseBrightness.bind(this);

    const initialState = {
      /* Snap SVG Instances */
      isDrawn: false,
      image: null,
      segInstanceGroup: null,
      boundingBoxGroup: null,

      pathsSortByCategories: new Map(),
      bboxesSortByCategories: new Map(),
      categories: [],

      /* SVG Canvas Config */
      opacity: 0.7,
      offsetWidth: 0,
      offsetHeight: 0,

      /* Visualization Configuration */
      segment: true,
      boundingBox: false,
      zoom: 1,
      brightness: 1,
      brightnessFilter: null
    };

    this.state = initialState;
  }

  componentDidMount() {
    this.fitImageToSVGContainer();
    this.drawImage();
    // this.drawInstance();
  }

  componentDidUpdate() {
    const {
      isDrawn,
      offsetWidth,
      offsetHeight,
      zoom,
      image,
      segInstanceGroup,
      boundingBoxGroup
    } = this.state;

    const { instances } = this.props;

    if (isDrawn) {
      image.transform(`translate(${[offsetWidth, offsetHeight]}) scale(${zoom})`);
      segInstanceGroup.transform(`translate(${[offsetWidth, offsetHeight]}) scale(${zoom})`);
      boundingBoxGroup.transform(`translate(${[offsetWidth, offsetHeight]}) scale(${zoom})`);
    } else if (Array.isArray(instances) && instances.length !== 0) {
      this.drawInstance();
      this.setupDragEvent();
    }
  }

  getColors() {
    return distinctColors({ count: this.props.instances.length })
      .map(({ _rgb: [r, g, b] }) => `rgba(${r}, ${g}, ${b}, ${this.state.opacity})`);
  }

  setupDragEvent() {
    const mousedown$ = Observable.fromEvent(this.svgElement, 'mousedown');
    const mousemove$ = Observable.fromEvent(document, 'mousemove');
    const mouseup$ = Observable.fromEvent(document, 'mouseup');
    const { svgElement } = this;
    const { x: svgLeft, y: svgTop } = svgElement.getBoundingClientRect();

    let mousePosition = null;

    mousedown$
      .switchMap(() => mousemove$.takeUntil(mouseup$))
      .map(({ clientX, clientY }) => ({
        x: clientX - svgLeft,
        y: clientY - svgTop
      }))
      .subscribe((newMousePosition) => {
        if (mousePosition) {
          const delta = {
            x: newMousePosition.x - mousePosition.x,
            y: newMousePosition.y - mousePosition.y
          };
          const { offsetWidth, offsetHeight } = this.state;
          this.setState({
            offsetWidth: offsetWidth + delta.x,
            offsetHeight: offsetHeight + delta.y
          });
        }
        mousePosition = newMousePosition;
      });

    mouseup$.subscribe(() => {
      mousePosition = null;
    });
  }

  drawInstance() {
    this.setState({ isDrawn: true });
    const { svg } = this;
    const { instances, instanceCategories } = this.props;
    const { categories, offsetWidth, offsetHeight } = this.state;

    const pathStyle = color => ({
      polygon: {
        fill: color
      },
      bbox: {
        fill: 'none',
        stroke: color,
        strokeWidth: 2
      }
    });
    // const bboxesStyle = color => ({
    //   fill: 'none',
    //   stroke: color,
    //   strokeWidth: 3
    // });

    const colors = this.getColors();

    const pathsSortByCategories = new Map(
      instanceCategories.map(category => ([category, []]))
    );
    const bboxesSortByCategories = new Map(
      instanceCategories.map(category => ([category, []]))
    );

    const segInstanceGroup = svg.g();
    segInstanceGroup.addClass('segmentation-instance-group');
    segInstanceGroup.transform(`translate(${[offsetWidth, offsetHeight]})`);

    const boundingBoxGroup = svg.g();
    boundingBoxGroup.addClass('hide');
    boundingBoxGroup.addClass('bounding-box-group');
    boundingBoxGroup.transform(`translate(${[offsetWidth, offsetHeight]})`);

    /* Instance Drawing */
    instances.forEach(({ umboCategory: { name: category }, seg, encoding }, i) => {
      categories.push(category);

      if (typeof seg[0] === 'number') return;

      /* Drawing Segmentaion */
      seg.forEach((segmentation) => {
        const pathNotation = chunk(segmentation, 2)
          .map(([x, y]) => `${x},${y}`)
          .join('L');
        const path = svg.path(`M${pathNotation}Z`);
        path
          .attr(pathStyle(colors[i])[encoding])
          .addClass('segmentation-instance')
          .data('category', category)
          .data('index', i);
        segInstanceGroup.add(path);
        pathsSortByCategories.get(category).push(path);
      });

      /* Drawing Bounding Box */
      // const [x1, y1, x2, y2] = bbox;
      // const width = x2 - x1;
      // const height = y2 - y1;
      // const rect = svg.rect(x1, y1, width, height);
      // rect
      //   .attr(bboxesStyle(colors[i]))
      //   .addClass('bounding-box-instance')
      //   .data('category', category)
      //   .data('index', i);
      // boundingBoxGroup.add(rect);
      // bboxesSortByCategories.get(category).push(rect);
    });

    this.setState({
      segInstanceGroup,
      boundingBoxGroup,
      pathsSortByCategories,
      bboxesSortByCategories
    });
  }

  drawImage() {
    const { svg } = this;
    const { url, width: imgWidth, height: imgHeight } = this.props;
    const { offsetWidth, offsetHeight } = this.state;

    const image = svg.image(url, 0, 0, imgWidth, imgHeight);
    image.addClass('segmentation-image');
    image.transform(`translate(${[offsetWidth, offsetHeight]})`);
    this.setState({ image });
  }

  fitImageToSVGContainer() {
    const { svgElement } = this;
    const { width: imgWidth, height: imgHeight } = this.props;
    const { clientWidth: svgWidth, clientHeight: svgHeight } = svgElement;

    const containerRatio = svgWidth / svgHeight;
    const imageRatio = imgWidth / imgHeight;
    let zoomRatio;
    if (imageRatio > containerRatio) {
      zoomRatio = svgWidth / imgWidth;
      this.setState({
        zoom: zoomRatio,
        offsetWidth: 0,
        offsetHeight: (svgHeight - (imgHeight * zoomRatio)) / 2
      });
    } else {
      zoomRatio = svgHeight / imgHeight;
      this.setState({
        zoom: zoomRatio,
        offsetWidth: (svgWidth - (imgWidth / zoomRatio)) / 2,
        offsetHeight: 0
      });
    }
  }

  handleToggleCategory({ checked, title: category }) {
    const {
      segment,
      pathsSortByCategories,
      boundingBox,
      bboxesSortByCategories
    } = this.state;

    const performMethod = checked ? 'removeClass' : 'addClass';

    if (segment) {
      pathsSortByCategories
        .get(category)
        .forEach(path => path[performMethod]('hide'));
    }

    if (boundingBox) {
      bboxesSortByCategories
        .get(category)
        .forEach(bbox => bbox[performMethod]('hide'));
    }
  }

  handleShowAllCategories() {
    const { categories } = this.state;
    categories.forEach((category) => {
      const { checkbox } = this[`category-${category}`];
      if (!checkbox.checked) { checkbox.click(); }
    });
  }

  handleHideAllCategories() {
    const { categories } = this.state;
    categories.forEach((category) => {
      const { checkbox } = this[`category-${category}`];
      if (checkbox.checked) { checkbox.click(); }
    });
  }

  handleSwitchToEvaluationPage() {
    const { history } = this.props;
    const fullPath = fullPathSelector(this.props);
    history.push(fullPath.replace(/\/segmentation/, '/evaluation'));
  }

  handleToggleSegmentVisibility() {
    this.setState({ segment: !this.state.segment }, function a() {
      const { segment, segInstanceGroup } = this.state;
      if (segment) {
        segInstanceGroup.removeClass('hide');
      } else {
        segInstanceGroup.addClass('hide');
      }
    });
  }

  handleToggleBoundingBoxVisibility() {
    this.setState({ boundingBox: !this.state.boundingBox }, function b() {
      const { boundingBox, boundingBoxGroup } = this.state;
      if (boundingBox) {
        boundingBoxGroup.removeClass('hide');
      } else {
        boundingBoxGroup.addClass('hide');
      }
    });
  }

  handleZoom(getNewZoomRateCallback) {
    return () => {
      const { zoom } = this.state;
      let { width: imgWidth, height: imgHeight } = this.props;
      const { svgElement: { clientWidth: svgWidth, clientHeight: svgHeight } } = this;
      const result = getNewZoomRateCallback(zoom); // zoom + rate > 3 ? 3 : (zoom + rate);
      imgWidth *= result;
      imgHeight *= result;
      const offsetWidth = (svgWidth - imgWidth) / 2;
      const offsetHeight = (svgHeight - imgHeight) / 2;
      this.setState({ zoom: result, offsetWidth, offsetHeight });
    };
  }

  handleIncreaseBrightness(rate = 0.25) {
    const { brightness } = this.state;
    this.changeBrightness(brightness + rate > 10 ? 10 : (brightness + rate));
  }

  handleDecreaseBrightness(rate = 0.25) {
    const { brightness } = this.state;
    this.changeBrightness(brightness - rate < 0 ? 0 : (brightness - rate));
  }

  changeBrightness(result) {
    const { svg } = this;
    const { image, brightnessFilter } = this.state;

    /* Remove the previous filter if exist */
    if (brightnessFilter) {
      const { node } = brightnessFilter;
      node.parentElement.removeChild(node);
    }

    const filter = svg.filter(Snap.filter.brightness(result));
    filter.attr({
      filterUnits: 'objectBoundingBox',
      x: '0%',
      y: '0%',
      width: '100%',
      height: '100%'
    });
    image.attr({ filter });
    this.setState({ brightness: result, brightnessFilter: filter });
  }

  render() {
    const { instanceCategories, flatButtonStyle } = this.props;
    const { segment } = this.state;

    const renderCategorySwitches = instanceCategories.map(category => (
      <AppSwitch
        key={category}
        title={category}
        defaultChecked
        onChange={this.handleToggleCategory}
        ref={(c) => { this[`category-${category}`] = c; }}
      />
    ));

    return (
      <div className="SegmentationSVG">
        <section className="segmentation-display">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            ref={(c) => {
              if (this.svg) {
                return;
              }
              this.svgElement = c;
              this.svg = Snap(c);
            }}
            width="100%"
            height="100%"
          />
        </section>

        <div className="control-panel">
          <div className="control-panel-tabs">
            <button className="active">Ground Truth</button>
          </div>

          <ListItemDrawer
            className="instance-drawer"
            title="Instance"
            defaultShow
            {...Hints.segmentation.toggleInstanceHint}
          >
            {renderCategorySwitches}

            <div className="btn-group">
              <FlatButton
                backgroundColor={teal300}
                style={flatButtonStyle}
                onClick={this.handleShowAllCategories}
              >
                <span className="fa fa-eye" /> Show All
              </FlatButton>

              <FlatButton
                backgroundColor={red300}
                style={flatButtonStyle}
                onClick={this.handleHideAllCategories}
              >
                <span className="fa fa-eye-slash" /> Hide All
              </FlatButton>
            </div>
          </ListItemDrawer>

          <ListItemDrawer
            title="Visualization Config"
            defaultShow
            className="visualization-config"
            {...Hints.segmentation.visualizationConfigHint}
          >

            <div className="config-wrapper">
              <button
                className={segment ? 'active' : ''}
                onClick={this.handleToggleSegmentVisibility}
              >
                <span className="fa fa-male" /> Ground Truth
              </button>
              {/*
              TODO: Implement back Bounding Box feature
              <button
                className={boundingBox ? 'active' : ''}
                onClick={this.handleToggleBoundingBoxVisibility}
              >
                <span className="fa fa-crop" /> Bounding Box
              </button>
              */}
              <br />
              <button
                onClick={this.handleZoom((zoom) => {
                  if (zoom + 0.1 > 3) return 3;
                  return (zoom + 0.1);
                })}
              >
                <span className="fa fa-search-plus" /> Zoom In
              </button>
              <button
                onClick={this.handleZoom((zoom) => {
                  if (zoom - 0.1 < 0.1) return 0.1;
                  return (zoom - 0.1);
                })}
              >
                <span className="fa fa-search-minus" /> Zoom Out
              </button>
              <button
                onClick={() => this.handleIncreaseBrightness()}
              >
                <span className="fa fa-star" /> Brighten
              </button>
              <button
                onClick={() => this.handleDecreaseBrightness()}
              >
                <span className="fa fa-star-o" /> Darken
              </button>
              {/* <div>TODO:</div>
            <div>Zoomable</div>
            <div>Change Instance Segmentation Opacity</div>
            <div>Change Brightness of the Image</div> */}
            </div>
          </ListItemDrawer>
        </div>
      </div>
    );
  }
}

SegmentationSVG.propTypes = {
  url: PropTypes.string.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  instanceCategories: PropTypes.arrayOf(PropTypes.string).isRequired,
  instances: PropTypes.arrayOf(PropTypes.shape({
    // bbox: PropTypes.arrayOf(PropTypes.number),
    seg: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
      PropTypes.arrayOf(PropTypes.number)
    ]),
    umboCategory: PropTypes.shape({
      name: PropTypes.string.isRequired
    })
  })).isRequired,
  history: PropTypes.object.isRequired,
  flatButtonStyle: PropTypes.object
};

SegmentationSVG.defaultProps = {
  flatButtonStyle: {
    color: 'white',
    fontSize: '10pt',
    marginLeft: '5pt',
    verticleAlign: 'top'
  }
};

function mapStateToProps(store) {
  const { SearchField: { imagePolygonInstances: instances } } = store;

  const instanceCategories = [
    ...instances.reduce(
      (acc, { umboCategory: { name } }) => acc.add(name),
      new Set(),
    )
  ];

  return {
    instances,
    instanceCategories
  };
}

export default withRouter(connect(mapStateToProps)(SegmentationSVG));



// WEBPACK FOOTER //
// ./src/containers/Main/SegmentationSVG.jsx